import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { V2PriceList } from '../../models/item-details-v2.models';

@Component({
    selector: 'app-v2-sidebar',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    template: `
        <aside class="v2-sidebar">
            <section class="v2-card v2-card--info">
                <div class="v2-info-heading" (click)="toggleInfo()">
                    <span class="v2-info-title">
                        <i class="pi pi-box"></i>
                        Item Information
                        <span *ngIf="displayItemNumber" class="v2-info-item-badge">{{ displayItemNumber }}</span>
                    </span>
                    <i class="pi" [ngClass]="infoExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
                </div>
                <ng-container *ngIf="infoExpanded">
                    <div class="v2-row v2-info-pill">
                        <span>Available Qty<span *ngIf="warehouseCode"> ({{ warehouseCode }})</span>:</span>
                        <strong>{{ availableQty | number: '1.0-2' }}</strong>
                    </div>
                    <div class="v2-row v2-info-pill"><span>Last Sales Price:</span><strong>{{ currencySymbol }}{{ (info?.lastSalePrice || 0) | number: '1.2-2' }}</strong></div>
                    <div class="v2-row v2-info-pill" *ngFor="let pl of priceLists"><span>{{ normalizePriceListName(pl?.name) }}:</span><strong>{{ currencySymbol }}{{ (pl?.value || 0) | number: '1.2-2' }}</strong></div>
                    <div class="v2-row v2-info-pill">
                        <span class="v2-expiry-label">
                            Earliest Expiry
                            <i
                                *ngIf="showExpiryStockInfo"
                                class="pi pi-info-circle v2-expiry-info"
                                [pTooltip]="expiryStockInfoMessage"
                                tooltipPosition="left"
                                (click)="$event.stopPropagation()"
                            ></i>:
                        </span>
                        <strong>{{ latestExpiryDisplay }}</strong>
                    </div>
                </ng-container>
            </section>

            <section class="v2-card v2-card--summary">
                <div class="v2-summary-header">
                    <h3><i class="pi pi-chart-bar"></i>ORDER SUMMARY</h3>
                    <span class="v2-summary-total">Total: <strong>{{ currencySymbol }}{{ grandTotal | number: '1.2-2' }}</strong></span>
                </div>
                <div class="v2-pill"><span>Total Articles:</span><strong>{{ itemCount }}</strong></div>
                <div class="v2-pill"><span>Total Weight:</span><strong>{{ totalWeight | number: '1.2-2' }}</strong></div>
                <div class="v2-pill"><span>Gross Value:</span><strong>{{ currencySymbol }}{{ grossValue | number: '1.2-2' }}</strong></div>
                <div class="v2-pill"><span>VAT Amount:</span><strong>{{ currencySymbol }}{{ vatAmount | number: '1.2-2' }}</strong></div>
                <div class="v2-pill v2-pill--grand"><span>Order Value:</span><strong>{{ currencySymbol }}{{ grandTotal | number: '1.2-2' }}</strong></div>
            </section>
        </aside>
    `,
    styles: [
        `
            .v2-sidebar {
                display: flex;
                flex-direction: column;
                gap: 0.9rem;
                position: sticky;
                top: 124px;
            }
            .v2-card {
                border: 1px solid var(--border-color, var(--p-surface-border));
                border-radius: 0.75rem;
                padding: 0.95rem 0.9rem;
                background: var(--surface-card, var(--p-surface-0));
                color: var(--text-color, var(--p-text-color));
                box-shadow: 0 1px 3px color-mix(in srgb, var(--text-color, #111) 12%, transparent);
            }
            .v2-card h3 {
                margin: 0 0 0.8rem;
                font-size: 1.45rem;
                line-height: 1;
                font-weight: 800;
                letter-spacing: 0.01em;
                color: var(--text-color, var(--p-text-color));
            }
            .v2-summary-header {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                gap: 0.5rem;
                margin-bottom: 0.5rem;
            }
            .v2-summary-header h3 {
                margin: 0;
                display: inline-flex;
                align-items: center;
                gap: 0.45rem;
                font-size: 1.12rem;
                font-weight: 800;
                line-height: 1.1;
                color: var(--primary-color, var(--p-primary-color));
            }
            .v2-summary-total {
                font-size: 0.82rem;
                font-weight: 700;
                color: var(--text-secondary, var(--p-text-muted-color));
                white-space: nowrap;
            }
            .v2-summary-total strong {
                color: var(--text-color, var(--p-text-color));
                font-weight: 800;
            }
            .v2-info-heading {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
                padding-bottom: 0.55rem;
                margin-bottom: 0.45rem;
                border-bottom: 1px solid var(--border-color, var(--p-surface-border));
                cursor: pointer;
                user-select: none;
            }
            .v2-info-title {
                display: inline-flex;
                align-items: center;
                gap: 0.45rem;
                font-size: 1.05rem;
                font-weight: 800;
                letter-spacing: 0.01em;
                color: var(--primary-color, var(--p-primary-color));
                flex-wrap: wrap;
            }
            .v2-info-item-badge {
                display: inline-flex;
                align-items: center;
                padding: 0.12rem 0.45rem;
                border-radius: 0.35rem;
                font-size: 0.78rem;
                font-weight: 900;
                letter-spacing: 0.02em;
                color: var(--primary-color, var(--p-primary-color));
                background: color-mix(in srgb, var(--primary-color, var(--p-primary-color)) 10%, transparent);
                border: 1px solid color-mix(in srgb, var(--primary-color, var(--p-primary-color)) 35%, transparent);
            }
            .v2-info-title .pi,
            .v2-summary-header h3 .pi {
                font-size: 0.9rem;
                color: var(--primary-color, var(--p-primary-color));
            }
            .v2-info-heading .pi {
                font-size: 0.9rem;
                color: var(--primary-color, var(--p-primary-color));
            }
            .v2-row {
                display: flex;
                justify-content: space-between;
                gap: 0.55rem;
                font-size: 1rem;
                line-height: 1.15;
                font-weight: 700;
                margin-top: 0.34rem;
                color: var(--text-color, var(--p-text-color));
            }
            .v2-row > span:first-child {
                font-weight: 700;
            }
            .v2-expiry-label {
                display: inline-flex;
                align-items: center;
                gap: 0.28rem;
            }
            .v2-expiry-info {
                font-size: 0.78rem;
                cursor: help;
                color: var(--primary-color, var(--p-primary-color));
            }
            .v2-row strong {
                font-weight: 900;
                color: var(--text-color, var(--p-text-color));
            }
            .v2-info-pill {
                margin-top: 0.5rem;
                padding: 0.42rem 0.6rem;
                border: 1px solid var(--border-color, var(--p-surface-border));
                border-radius: 0.5rem;
                background: var(--surface-section, var(--p-surface-50));
                box-shadow: 0 1px 2px color-mix(in srgb, var(--text-color, #111) 10%, transparent);
            }
            .v2-card--summary {
                border-top: 1px solid var(--border-color, var(--p-surface-border));
                padding-top: 0.9rem;
            }
            .v2-pill {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 0.5rem;
                margin-top: 0.38rem;
                padding: 0.34rem 0.5rem;
                border: 1px solid var(--border-color, var(--p-surface-border));
                border-radius: 0.5rem;
                background: var(--surface-section, var(--p-surface-50));
                box-shadow: 0 1px 2px color-mix(in srgb, var(--text-color, #111) 10%, transparent);
                color: var(--text-color, var(--p-text-color));
                font-size: 0.94rem;
                line-height: 1.05;
            }
            .v2-pill span {
                font-weight: 800;
            }
            .v2-pill strong {
                font-weight: 900;
                color: var(--text-color, var(--p-text-color));
            }
            .v2-pill--grand {
                background: color-mix(in srgb, var(--primary-color) 10%, var(--surface-section, var(--p-surface-50)));
                border-color: color-mix(in srgb, var(--primary-color) 35%, var(--border-color, var(--p-surface-border)));
            }

            /* Dark theme: keep yellow highlight on numeric values. */
            :host-context(.app-dark) .v2-row strong,
            :host-context(html.app-dark) .v2-row strong,
            :host-context(.app-dark) .v2-pill strong,
            :host-context(html.app-dark) .v2-pill strong,
            :host-context(.app-dark) .v2-summary-total strong,
            :host-context(html.app-dark) .v2-summary-total strong,
            :host-context(.app-dark) .v2-info-item-badge,
            :host-context(html.app-dark) .v2-info-item-badge {
                color: #facc15;
            }
            :host-context(.app-dark) .v2-info-item-badge,
            :host-context(html.app-dark) .v2-info-item-badge {
                background: color-mix(in srgb, #facc15 18%, transparent);
                border-color: color-mix(in srgb, #facc15 45%, transparent);
            }
            :host-context(.app-dark) .v2-pill,
            :host-context(html.app-dark) .v2-pill {
                background: var(--surface-overlay, var(--p-surface-800));
            }
            :host-context(.app-dark) .v2-info-pill,
            :host-context(html.app-dark) .v2-info-pill {
                background: var(--surface-overlay, var(--p-surface-800));
            }
            @media (max-width: 768px) {
                .v2-sidebar {
                    position: static;
                    gap: 0.65rem;
                }
                .v2-card {
                    padding: 0.65rem 0.6rem;
                }
                .v2-card h3 {
                    font-size: 1.1rem;
                    margin-bottom: 0.55rem;
                }
                .v2-summary-header {
                    margin-bottom: 0.4rem;
                }
                .v2-summary-header h3 {
                    font-size: 0.98rem;
                }
                .v2-summary-total {
                    font-size: 0.74rem;
                }
                .v2-row {
                    font-size: 0.88rem;
                    margin-top: 0.28rem;
                }
                .v2-pill {
                    font-size: 0.82rem;
                    padding: 0.32rem 0.44rem;
                }
            }
        `
    ]
})
export class V2SidebarComponent {
    @Input() info: any;
    @Input() priceLists: V2PriceList[] = [];
    @Input() selectedItemName = '';
    @Input() selectedItemNumber = '';
    @Input() infoContext: 'line' | 'addRow' | 'none' = 'none';
    @Input() availableQty = 0;
    @Input() itemCount = 0;
    @Input() grossValue = 0;
    @Input() vatAmount = 0;
    @Input() grandTotal = 0;
    @Input() totalWeight = 0;
    @Input() currencySymbol = '€';

    infoExpanded = false;
    readonly expiryStockInfoMessage =
        'This quantity is from M3 lots for this expiry date. Stock already used in Torna may not be deducted until the order is synced to M3.';

    get displayItemNumber(): string {
        return String(this.selectedItemNumber ?? '').trim();
    }

    toggleInfo(): void {
        this.infoExpanded = !this.infoExpanded;
    }

    setInfoExpanded(expanded: boolean): void {
        this.infoExpanded = expanded;
    }

    get warehouseCode(): string {
        return `${this.info?.warehouse ?? this.info?.whsId ?? this.info?.warehouseId ?? ''}`;
    }

    get showExpiryStockInfo(): boolean {
        const qty = this.info?.expiryStockQty;
        if (qty === null || qty === undefined || qty === '') {
            return false;
        }
        const numericQty = Number(qty);
        return !Number.isNaN(numericQty) && numericQty > 0;
    }

    get latestExpiryDisplay(): string {
        const date = String(this.info?.expiryDate ?? '').trim();
        const qty = this.info?.expiryStockQty;
        if (!date || qty === null || qty === undefined || qty === '') {
            return 'N/A';
        }
        const numericQty = Number(qty);
        if (Number.isNaN(numericQty) || numericQty <= 0) {
            return 'N/A';
        }
        return `${date}  (${numericQty})`;
    }

    normalizePriceListName(name?: string): string {
        if (!name) {
            return 'Price List';
        }
        if (name.startsWith('Price List')) {
            return name;
        }
        return `Price List ${name}`;
    }
}











